import { specialDietStudentLabel } from '@ordo/constants/special-diet-student-label'
import { fileToBase64 } from '@ordo/ui'
import { Button } from '@ordo/ui/button'
import { Checkbox } from '@ordo/ui/checkbox'
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@ordo/ui/dialog'
import { FileInput } from '@ordo/ui/file-input'
import { Input } from '@ordo/ui/input'
import { Label } from '@ordo/ui/label'
import { LoadingButton } from '@ordo/ui/loading-button'
import { RadioGroup, RadioGroupItem } from '@ordo/ui/radio-group'
import { type SearchableSelectOption } from '@ordo/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { Skeleton } from '@ordo/ui/skeleton'
import { Textarea } from '@ordo/ui/textarea'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { authHeaders } from '@/api/http'
import { type AdminSchool, useMySchoolAdminLocationsQuery } from '@/api/school-orders'
import {
  type DietaryTagOption,
  NOTE_ACCEPT,
  NOTE_MAX_BYTES,
  noteFileTooLarge,
  type SpecialDietAccommodationWire,
  type StudentSearchResultWire,
  type SubmitAccommodationInput,
  useNoteOptionsQuery,
} from '@/api/special-diet-accommodations'
import { type Mealtime, MEALTIME_LABELS, MEALTIMES } from '@/lib/mealtime'

import { SpecialDietNoteSuggestions } from './special-diet-note-suggestions'
import { StudentRosterPicker } from './student-roster-picker'

export function splitIntakeNotes(value?: string | null) {
  let foodsToOmit = ''
  let suggestedSubstitutions = ''
  const notes: string[] = []
  for (const section of (value ?? '').split('\n\n')) {
    if (section.startsWith('Foods to be omitted: ')) {
      foodsToOmit = section.slice('Foods to be omitted: '.length)
    } else if (section.startsWith('Suggested substitutions: ')) {
      suggestedSubstitutions = section.slice('Suggested substitutions: '.length)
    } else {
      notes.push(section)
    }
  }
  return { foodsToOmit, suggestedSubstitutions, notes: notes.join('\n\n') }
}

export type AddNoteValues = Omit<SubmitAccommodationInput, 'note'> & { note?: SubmitAccommodationInput['note'] }

/** Sentinel value for the "nothing here fits" option, pinned to the bottom. */
export const CUSTOM_RESTRICTION = '__custom__'

/**
 * The restriction picker's options: the canonical allergen and dietary tags
 * the menu builder uses, then Custom last.
 *
 * Custom exists because the alternative is worse. Without it an office facing
 * an unlisted restriction types it into whatever field is nearest, and free
 * text does not fail loudly here — the compliance plan simply cannot parse it,
 * and the child's diet becomes silently unverifiable. An explicit Custom
 * routes the same words to a human instead.
 */
export function buildRestrictionOptions(
  allergens: DietaryTagOption[] | undefined,
  dietary: DietaryTagOption[] | undefined,
): SearchableSelectOption[] {
  const toOption = (tag: DietaryTagOption): SearchableSelectOption => ({
    value: tag.slug,
    label: tag.name,
    searchText: tag.slug.replace(/-/g, ' '),
  })
  return [
    ...(allergens ?? []).map(toOption),
    ...(dietary ?? []).map(toOption),
    { value: CUSTOM_RESTRICTION, label: 'Custom — not listed above' },
  ]
}

/**
 * Split the picked slugs into the two columns the accommodation stores.
 *
 * Allergen-kind tags are things the food CONTAINS and the child must avoid;
 * everything else is a claim the food must carry. They are read by different
 * halves of the compliance plan, so filing one as the other makes it
 * unverifiable. The CUSTOM sentinel is never a slug and is dropped here.
 */
export function splitRestrictionSlugs(
  values: string[],
  allergens: DietaryTagOption[],
): { restrictedAllergens: string[]; dietaryNeeds: string[] } {
  const allergenSlugs = new Set(allergens.map((tag) => tag.slug))
  const picked = values.filter((value) => value !== CUSTOM_RESTRICTION)
  return {
    restrictedAllergens: picked.filter((slug) => allergenSlugs.has(slug)),
    dietaryNeeds: picked.filter((slug) => !allergenSlugs.has(slug)),
  }
}

export function AddNoteDialog(props: {
  amendment?: boolean
  accommodation?: SpecialDietAccommodationWire
  open: boolean
  onOpenChange: (open: boolean) => void
  school: AdminSchool
  pending: boolean
  error: string | null
  onSubmit: (values: AddNoteValues) => Promise<void>
}) {
  // The office has the student's NAME, not a cuid and often not a student
  // number either. Search by name; pick when more than one matches.
  const existing = props.accommodation
  const storedNote = useQuery({
    queryKey: ['special-diet', 'note-file', existing?.doctorNoteDocumentId],
    enabled: Boolean(existing?.doctorNoteDocumentId),
    gcTime: 0,
    queryFn: async ({ signal }) => {
      const response = await fetch(
        `/api/special-diet/documents/${encodeURIComponent(existing!.doctorNoteDocumentId!)}/download`,
        { headers: authHeaders(), signal },
      )
      if (!response.ok) {
        throw new Error('Unable to load the attached note. You can upload a replacement.')
      }
      const blob = await response.blob()
      return new File([blob], 'Attached doctor note', { type: blob.type })
    },
  })
  const initialNotes = splitIntakeNotes(existing?.notes)
  const [picked, setPicked] = useState<StudentSearchResultWire | null>(
    existing
      ? { userId: existing.studentUserId ?? '', name: specialDietStudentLabel(existing), grade: null, studentId: null }
      : null,
  )
  const [addingStudent, setAddingStudent] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [localStudentId, setLocalStudentId] = useState('')
  const [grade, setGrade] = useState('')
  const [termId, setTermId] = useState('')
  const [parentOrderingEnabled, setParentOrderingEnabled] = useState(existing?.parentOrderingEnabled ?? false)
  const [parentContactEmail, setParentContactEmail] = useState(existing?.parentContactEmail ?? '')
  const [parentContactFirstName, setParentContactFirstName] = useState(existing?.parentContactFirstName ?? '')
  const [parentContactLastName, setParentContactLastName] = useState(existing?.parentContactLastName ?? '')
  const [notes, setNotes] = useState(initialNotes.notes)
  const [foodsToOmit, setFoodsToOmit] = useState(initialNotes.foodsToOmit)
  const [suggestedSubstitutions, setSuggestedSubstitutions] = useState(initialNotes.suggestedSubstitutions)
  const [campusId, setCampusId] = useState(props.school.id)
  const campuses = useMySchoolAdminLocationsQuery()
  const locationId = addingStudent ? campusId : props.school.id
  const [intakeKey] = useState(() => crypto.randomUUID())
  const [step, setStep] = useState(0)
  // Deliberately NOT pre-seeded with LUNCH. A restriction usually spans every
  // meal the child eats at school, and a default would silently reproduce the
  // lunch-only accommodation this field was added to fix.
  const [mealtimes, setMealtimes] = useState<Mealtime[]>(
    (existing?.mealtimes ?? []).filter((meal): meal is Mealtime => MEALTIMES.includes(meal as Mealtime)),
  )
  const [file, setFile] = useState<File | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)

  const options = useNoteOptionsQuery(locationId)
  // Only the meals this campus serves. Offering the rest lets an office file a
  // note covering Supper at a lunch-only school — an accommodation that can
  // never be approved, because approval needs a menu for every covered meal.
  const servedMealtimes = MEALTIMES.filter((meal) => (options.data?.mealtimes ?? []).includes(meal))
  const tooLarge = file ? noteFileTooLarge(file) : false
  const canSubmit =
    (!existing || Boolean(existing.updatedAt)) &&
    (addingStudent ? firstName.trim().length > 0 && lastName.trim().length > 0 : picked !== null) &&
    (!addingStudent || !grade || Boolean(termId || options.data?.terms?.length === 1)) &&
    (!parentOrderingEnabled ||
      Boolean(parentContactFirstName.trim() && parentContactLastName.trim() && parentContactEmail.trim())) &&
    options.data?.specialDietsEnabled !== false &&
    !options.isPending &&
    !options.error &&
    mealtimes.length > 0 &&
    mealtimes.every((meal) => servedMealtimes.includes(meal)) &&
    (foodsToOmit.trim().length > 0 || suggestedSubstitutions.trim().length > 0 || notes.trim().length > 0) &&
    (file !== null || Boolean(existing?.doctorNoteDocumentId)) &&
    !tooLarge

  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        if (!props.pending) {
          props.onOpenChange(open)
        }
      }}>
      <DialogContent className="sm:max-w-2xl pb-0">
        {/* Native form so Enter submits from any single-line input. */}
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={async (e) => {
            e.preventDefault()
            if (step < 3) {
              return
            }
            if (!canSubmit) {
              return
            }
            setLocalError(null)
            try {
              await props.onSubmit({
                organizationId: props.school.organizationId,
                locationId,
                studentUserId: addingStudent ? null : (picked?.userId ?? null),
                newStudent: addingStudent
                  ? {
                      firstName: firstName.trim(),
                      lastName: lastName.trim(),
                      localStudentId: localStudentId.trim() || undefined,
                      grade: grade || undefined,
                      termId: grade
                        ? termId || (options.data?.terms?.length === 1 ? options.data.terms[0]!.id : undefined)
                        : undefined,
                    }
                  : undefined,
                parentContactFirstName: parentOrderingEnabled ? parentContactFirstName.trim() : null,
                parentContactLastName: parentOrderingEnabled ? parentContactLastName.trim() : null,
                notes:
                  [
                    foodsToOmit.trim() ? `Foods to be omitted: ${foodsToOmit.trim()}` : null,
                    suggestedSubstitutions.trim() ? `Suggested substitutions: ${suggestedSubstitutions.trim()}` : null,
                    notes.trim(),
                  ]
                    .filter(Boolean)
                    .join('\n\n') || null,
                parentContactEmail: parentOrderingEnabled ? parentContactEmail.trim() : null,
                parentOrderingEnabled,
                acceptableMilkOptionCodes: [],
                intakeKey,
                mealtimes,
                restrictedAllergens: [],
                dietaryNeeds: [],
                note: file ? { fileName: file.name, contentBase64: await fileToBase64(file) } : undefined,
              })
            } catch {
              // Surfaced via props.error from the mutation.
            }
          }}>
          <DialogHeader>
            <DialogTitle>
              {props.amendment
                ? `Amend special diet request · V${(existing?.revision ?? 1) + 1}`
                : existing
                  ? `Edit special diet request · V${existing.revision ?? 1}`
                  : 'Request special diet meals'}
            </DialogTitle>
            {props.amendment && (
              <p className="text-sm text-muted-foreground">
                You’re editing a new revision. The approved plan remains unchanged until the dietitian approves this
                revision.
              </p>
            )}
            <ol aria-label="Request progress" className="grid grid-cols-4 gap-2 pt-4">
              {['Student', 'Doctor’s note', 'Family details', 'Review and confirm'].map((label, index) => (
                <li key={label} aria-current={step === index ? 'step' : undefined} className="space-y-2">
                  <div className={`h-1 rounded-full ${index <= step ? 'bg-primary' : 'bg-muted'}`} />
                  <span
                    className={`text-xs ${step === index ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                    {label}
                  </span>
                </li>
              ))}
            </ol>
          </DialogHeader>
          <fieldset disabled={props.pending} className="contents">
            <DialogBody className="flex min-h-64 flex-col gap-6 py-6">
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Step {step + 1} of 4
                </p>
                <h3 className="text-xl font-semibold">
                  {
                    [
                      'Who needs special diet meals?',
                      'Upload the doctor’s note',
                      'Confirm student and family details',
                      'Review and Confirm',
                    ][step]
                  }
                </h3>
                <p className="text-sm text-muted-foreground">
                  {
                    [
                      existing
                        ? 'Review the student for this request.'
                        : 'Choose a student from your school roster or add someone new.',
                      'Add a PDF, JPEG, or PNG, up to 10 MB. Our Registered Dietitian Nutritionist will review it.',
                      '',
                      'Check the details against the doctor’s note before submitting for review.',
                    ][step]
                  }
                </p>
              </div>
              <div hidden={step !== 0} className="space-y-4">
                {existing ? (
                  <p className="rounded-lg border p-4 font-medium">{specialDietStudentLabel(existing)}</p>
                ) : (
                  <>
                    <div className="flex flex-col gap-1.5">
                      <RadioGroup
                        aria-label="Student source"
                        className="grid gap-3 sm:grid-cols-2"
                        value={addingStudent ? 'new' : 'roster'}
                        onValueChange={(value) => {
                          setAddingStudent(value === 'new')
                          setPicked(null)
                          setMealtimes([])
                        }}>
                        {[false, true].map((isNew) => (
                          <Label
                            key={String(isNew)}
                            htmlFor={isNew ? 'sd-source-new' : 'sd-source-roster'}
                            className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${addingStudent === isNew ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}>
                            <RadioGroupItem
                              value={isNew ? 'new' : 'roster'}
                              id={isNew ? 'sd-source-new' : 'sd-source-roster'}
                              className="mt-1 shrink-0"
                            />
                            <span className="space-y-1">
                              <span className="block text-sm font-semibold">
                                {isNew ? 'Add a new student' : 'Choose from roster'}
                              </span>
                              <span className="block text-xs text-muted-foreground">
                                {isNew
                                  ? 'For a student not yet listed at your school.'
                                  : 'Find a student already enrolled at your school.'}
                              </span>
                            </span>
                          </Label>
                        ))}
                      </RadioGroup>
                      {addingStudent ? (
                        <p className="text-muted-foreground text-sm">
                          Next, upload their doctor’s note. Then we’ll collect their name and optional parent details.
                        </p>
                      ) : (
                        <div>
                          <StudentRosterPicker
                            locationId={props.school.id}
                            schoolName={props.school.name}
                            picked={picked}
                            onPick={setPicked}
                            inputId="sd-student"
                            emptyRosterHint="Choose Add a new student, or check with the school."
                            ambiguousHint="Confirm which one with the office before adding the note."
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div hidden={step !== 1} className="space-y-4">
                <div className="flex flex-col gap-1.5">
                  <Label>The note (PDF, JPEG, or PNG)</Label>
                  {existing?.doctorNoteDocumentId && !file && (
                    <p className="text-sm text-muted-foreground">
                      The current doctor’s note is attached. Upload a file to replace it.
                    </p>
                  )}
                  {storedNote.error && (
                    <p role="alert" className="text-sm text-destructive">
                      {storedNote.error.message}
                    </p>
                  )}
                  <FileInput
                    value={file ? [file] : []}
                    onChange={(files) => {
                      setFile(files[0] ?? null)
                      setLocalError(null)
                    }}
                    accept={NOTE_ACCEPT}
                    multiple={false}
                    maxFiles={1}
                    maxSize={NOTE_MAX_BYTES}
                  />
                  {tooLarge ? (
                    <span className="text-destructive text-xs">That file is over the 10 MB limit.</span>
                  ) : null}
                </div>
              </div>
              <div hidden={step !== 2} className="space-y-4">
                {addingStudent ? (
                  <div className="grid gap-2">
                    <Label htmlFor="sd-campus">Campus</Label>
                    <Select
                      value={campusId}
                      onValueChange={(value) => {
                        setCampusId(value)
                        setGrade('')
                        setTermId('')
                        setMealtimes([])
                      }}>
                      <SelectTrigger id="sd-campus">
                        <SelectValue placeholder="Choose campus" />
                      </SelectTrigger>
                      <SelectContent>
                        {[
                          props.school,
                          ...(campuses.data?.me?.schoolAdminLocations ?? []).filter(
                            (campus) =>
                              campus.organizationId === props.school.organizationId && campus.id !== props.school.id,
                          ),
                        ].map((campus) => (
                          <SelectItem key={campus.id} value={campus.id}>
                            {campus.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Label htmlFor="sd-first">First name</Label>
                    <Input id="sd-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                    <Label htmlFor="sd-last">Last name</Label>
                    <Input id="sd-last" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                    {Boolean(options.data?.grades?.length && options.data?.terms?.length) && (
                      <>
                        <Label htmlFor="sd-grade">Grade (Optional)</Label>
                        <Select
                          value={grade || '__none__'}
                          onValueChange={(value) => {
                            setGrade(value === '__none__' ? '' : value)
                            setTermId('')
                          }}>
                          <SelectTrigger id="sd-grade">
                            <SelectValue placeholder="No grade selected" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">No grade selected</SelectItem>
                            {options.data?.grades?.map((value) => (
                              <SelectItem key={value} value={value}>
                                {value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {grade && (options.data?.terms?.length ?? 0) > 1 && (
                          <>
                            <Label htmlFor="sd-term">School term</Label>
                            <Select value={termId} onValueChange={setTermId}>
                              <SelectTrigger id="sd-term">
                                <SelectValue placeholder="Choose term" />
                              </SelectTrigger>
                              <SelectContent>
                                {options.data?.terms?.map((term) => (
                                  <SelectItem key={term.id} value={term.id}>
                                    {term.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </>
                        )}
                        <p className="text-xs text-muted-foreground">
                          This sets up the student’s roster grade so approved meals use the right age-group menu.
                        </p>
                      </>
                    )}

                    <Label htmlFor="sd-local-id">School student ID (optional)</Label>
                    <Input
                      id="sd-local-id"
                      value={localStudentId}
                      onChange={(e) => setLocalStudentId(e.target.value)}
                    />
                  </div>
                ) : (
                  <p>Student: {picked?.name}</p>
                )}
                <fieldset className="space-y-2">
                  <legend className="text-sm font-medium">Allow the parent to view and order these meals?</legend>
                  <p className="text-xs text-muted-foreground">
                    After approval, these choices appear in the linked parent’s normal ordering page. No keeps ordering
                    with the school. A parent can still view their child’s accommodation and meal history.
                  </p>
                  <RadioGroup
                    aria-label="Parent ordering access"
                    value={parentOrderingEnabled ? 'parent' : 'school'}
                    onValueChange={(value) => setParentOrderingEnabled(value === 'parent')}
                    className="gap-3 sm:grid-cols-2">
                    {[false, true].map((enabled) => (
                      <Label
                        key={String(enabled)}
                        htmlFor={enabled ? 'sd-order-parent' : 'sd-order-school'}
                        className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 font-normal has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5">
                        <RadioGroupItem
                          id={enabled ? 'sd-order-parent' : 'sd-order-school'}
                          value={enabled ? 'parent' : 'school'}
                        />
                        {enabled ? 'Yes — parent can order' : 'No — school orders'}
                      </Label>
                    ))}
                  </RadioGroup>
                </fieldset>
                {parentOrderingEnabled && (
                  <div className="space-y-3">
                    <Label htmlFor="sd-parent-first">Parent first name</Label>
                    <Input
                      required
                      id="sd-parent-first"
                      value={parentContactFirstName}
                      onChange={(e) => setParentContactFirstName(e.target.value)}
                    />
                    <Label htmlFor="sd-parent-last">Parent last name</Label>
                    <Input
                      required
                      id="sd-parent-last"
                      value={parentContactLastName}
                      onChange={(e) => setParentContactLastName(e.target.value)}
                    />
                    <Label htmlFor="sd-parent">Parent email</Label>
                    <Input
                      required
                      id="sd-parent"
                      type="email"
                      value={parentContactEmail}
                      onChange={(e) => setParentContactEmail(e.target.value)}
                    />
                  </div>
                )}
              </div>
              <div hidden={step !== 3} className="space-y-4">
                {!tooLarge && (
                  <SpecialDietNoteSuggestions
                    file={file ?? storedNote.data ?? null}
                    locationId={locationId}
                    onApply={(suggestions) => {
                      if (addingStudent) {
                        if (!firstName.trim()) {
                          setFirstName(suggestions.studentFirstName ?? '')
                        }
                        if (!lastName.trim()) {
                          setLastName(suggestions.studentLastName ?? '')
                        }
                      }
                      setFoodsToOmit(
                        (current) => current || suggestions.foodsToOmit || suggestions.restrictedAllergens.join(', '),
                      )
                      setSuggestedSubstitutions((current) => current || suggestions.suggestedSubstitutions || '')
                      if (suggestions.notes) {
                        setNotes((current) => current || suggestions.notes || '')
                      }
                    }}
                  />
                )}
                <div className="space-y-2">
                  <Label htmlFor="sd-omitted">Foods to be Omitted</Label>
                  <Textarea
                    id="sd-omitted"
                    value={foodsToOmit}
                    onChange={(event) => setFoodsToOmit(event.target.value)}
                    placeholder="Review extracted text or enter foods from the note"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sd-substitutions">Suggested substitutions</Label>
                  <Textarea
                    id="sd-substitutions"
                    value={suggestedSubstitutions}
                    onChange={(event) => setSuggestedSubstitutions(event.target.value)}
                    placeholder="Review extracted text or enter substitutions from the note"
                  />
                </div>
                <fieldset className="flex flex-col gap-1.5">
                  <legend className="text-sm leading-none font-medium">Which meals does this apply to?</legend>
                  {options.isPending ? (
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <Skeleton className="h-5 w-28" />
                      <Skeleton className="h-5 w-28" />
                    </div>
                  ) : servedMealtimes.length === 0 ? (
                    <span className="text-muted-foreground text-sm">
                      No meal service is set up for this school yet, so a note can&apos;t be filed. Contact Ordo.
                    </span>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      {servedMealtimes.map((meal) => (
                        <label key={meal} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={mealtimes.includes(meal)}
                            onCheckedChange={(checked) =>
                              setMealtimes((current) =>
                                // Rebuild from MEALTIMES so the stored order is
                                // chronological, not click order.
                                checked === true
                                  ? MEALTIMES.filter((m) => m === meal || current.includes(m))
                                  : current.filter((m) => m !== meal),
                              )
                            }
                          />
                          {MEALTIME_LABELS[meal]}
                        </label>
                      ))}
                    </div>
                  )}
                </fieldset>
                <Label htmlFor="sd-notes">Notes (optional)</Label>
                <Textarea
                  id="sd-notes"
                  rows={5}
                  className="min-h-32 resize-y [field-sizing:content]"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Anything else the Registered Dietitian Nutritionist needs to know"
                />
                {(props.error ?? localError) ? (
                  <span className="text-destructive text-sm">{props.error ?? localError}</span>
                ) : null}
              </div>
              {options.data?.specialDietsEnabled === false ? (
                <p className="text-destructive text-sm">
                  Special Diets is disabled for this school. Contact Ordo to enable it.
                </p>
              ) : null}
            </DialogBody>
          </fieldset>
          <div className="flex shrink-0 items-center justify-between gap-3 border-t py-4">
            {step > 0 ? (
              <Button type="button" variant="outline" disabled={props.pending} onClick={() => setStep(step - 1)}>
                Back
              </Button>
            ) : (
              <span />
            )}
            {step < 3 ? (
              <Button
                type="button"
                disabled={
                  props.pending ||
                  (step === 0 && !addingStudent && !picked) ||
                  (step === 1 && ((!file && !existing?.doctorNoteDocumentId) || tooLarge)) ||
                  (step === 2 &&
                    ((addingStudent &&
                      (!firstName.trim() ||
                        !lastName.trim() ||
                        (grade && !termId && options.data?.terms?.length !== 1))) ||
                      (parentOrderingEnabled &&
                        (!parentContactFirstName.trim() ||
                          !parentContactLastName.trim() ||
                          !parentContactEmail.trim()))))
                }
                onClick={(event) => {
                  if (step === 2 && !event.currentTarget.form?.reportValidity()) {
                    return
                  }
                  setStep(step + 1)
                }}>
                Continue
              </Button>
            ) : (
              <LoadingButton type="submit" loading={props.pending} disabled={!canSubmit}>
                {props.amendment
                  ? 'Submit revision for review'
                  : existing
                    ? 'Resubmit for review'
                    : 'Submit for review'}
              </LoadingButton>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
